import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllProductsShop } from "../../redux/actions/product";
import { addToWishlist, removeFromWishlist } from "../../redux/actions/wishlist";
import { addTocart } from "../../redux/actions/cart";
import Breadcrumbs from "../Layout/Breadcrumbs";
import { useToast } from "../Layout/Toast/ToastProvider.jsx";
import {
  AiFillHeart,
  AiOutlineHeart,
  AiOutlineLeft,
  AiOutlineRight,
  AiOutlineClose,
  AiOutlineExpand,
} from "react-icons/ai";
import "./SubProductDetails.scss";

const SubProductDetails = ({ subProduct }) => {
  const dispatch = useDispatch();
  const { wishlist } = useSelector((state) => state.wishlist);
  const { cart } = useSelector((state) => state.cart);
  const { products } = useSelector((state) => state.products);
  const { showToast } = useToast();

  const [count] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [hoveredColor, setHoveredColor] = useState(null);
  const [animateHeart, setAnimateHeart] = useState(false);

  const [totalOriginalPrice, setTotalOriginalPrice] = useState(
    subProduct?.price || 0
  );
  const [totalDiscountPrice, setTotalDiscountPrice] = useState(
    subProduct?.discountPrice || 0
  );

  const [isInWishlist, setIsInWishlist] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const [startX, setStartX] = useState(null);

  const parentProduct = useMemo(() => {
    if (!subProduct || !products?.length) return null;
    for (let prod of products) {
      const foundSubProd = prod.subProducts?.find(
        (sp) => sp._id === subProduct._id
      );
      if (foundSubProd) {
        return prod;
      }
    }
    return null;
  }, [subProduct, products]);

  useEffect(() => {
    if (subProduct?.price && subProduct?.discountPrice) {
      setTotalOriginalPrice(subProduct.price * count);
      setTotalDiscountPrice(subProduct.discountPrice * count);
    } 
    else if (subProduct?.price) {
      setTotalOriginalPrice(subProduct.price * count);
      setTotalDiscountPrice(0); 
    }
  }, [count, subProduct]);

  useEffect(() => {
    if (subProduct?.shop?._id) {
      dispatch(getAllProductsShop(subProduct.shop._id));
    }

    if (wishlist && wishlist.some((item) => item._id === subProduct._id)) {
      setIsInWishlist(true);
    } else {
      setIsInWishlist(false);
    }
  }, [subProduct, wishlist, dispatch]);

  const handleColorClick = (color) => {
    setSelectedColor(color);
  };

  const handleAddToWishlist = () => {
    setAnimateHeart(true);
    setIsInWishlist(true);
    dispatch(addToWishlist(subProduct));
    setTimeout(() => setAnimateHeart(false), 400);
  };

  const handleRemoveFromWishlist = () => {
    setIsInWishlist(false);
    dispatch(removeFromWishlist(subProduct));
  };

  const handleAddToCart = () => {
    if (!subProduct) return;
    const itemIndex = cart.findIndex((item) => item._id === subProduct._id);

    if (itemIndex !== -1) {
      const updatedItem = {
        ...cart[itemIndex],
        qty: cart[itemIndex].qty + 1,
      };
      dispatch(addTocart(updatedItem, itemIndex));
      showToast("Кількість товару збільшено!", "success");
    } else {
      const cartData = {
        ...subProduct,
        originalPrice: subProduct.price,
        discountPrice: subProduct.discountPrice,
        qty: count,
      };
      dispatch(addTocart(cartData));
      showToast("Товар додано до кошика!", "success");
    }
  };
  const handleNextImage = () => {
    if (!subProduct?.images?.length) return; 
    setCurrentImageIndex((prev) => (prev + 1) % subProduct?.images?.length);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(
      (prev) =>
        (prev - 1 + (subProduct?.images?.length || 1)) %
        (subProduct?.images?.length || 1)
    );
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsFullscreen(false);
        setIsAnimating(false);
      }, 400);
    } else {
      setIsFullscreen(true);
    }
  };

  const handleSwipeStart = (clientX) => {
    setStartX(clientX);
  };

  const handleSwipeMove = (clientX) => {
    if (startX !== null) {
      const distance = clientX - startX;
      if (distance > 50) {
        handlePrevImage();
        setStartX(null);
      } else if (distance < -50) {
        handleNextImage();
        setStartX(null);
      }
    }
  };

  const handleSwipeEnd = () => {
    setStartX(null);
  };

  const hasDiscount =
    subProduct.discountPrice && subProduct.discountPrice !== subProduct.price;

  const formatPrice = (price) => (price ? price.toLocaleString("uk-UA") : "0");

   if (!subProduct) {
    return (
      <div className="subProductDetails__empty">
        Суб-продукт недоступний
      </div>
    );
  }

  if (!subProduct.images || !subProduct.images.length) {
    return <div>Немає зображень для відображення.</div>;
  }


  return (
    <div className="subProductDetails">
      <div className="subProductDetails__wrapper">
        <Breadcrumbs
          data={parentProduct ? { ...parentProduct, subProduct } : subProduct}
        />

        <div className="subProductDetails__content">
          <div className="subProductDetails__imagesContainer">
            <div className="subProductDetails__leftBlock">
              <div
                className="subProductDetails__mainImageWrapper"
                onTouchStart={(e) => handleSwipeStart(e.touches[0].clientX)}
                onTouchMove={(e) => handleSwipeMove(e.touches[0].clientX)}
                onTouchEnd={handleSwipeEnd}
                onMouseDown={(e) => handleSwipeStart(e.clientX)}
                onMouseMove={(e) => handleSwipeMove(e.clientX)}
                onMouseUp={handleSwipeEnd}
                onMouseLeave={handleSwipeEnd}
                onDragStart={(e) => e.preventDefault()}
              >
                <img
                  src={subProduct.images?.[currentImageIndex]?.url}
                  alt=""
                  className="subProductDetails__mainImage"
                />

                {!isFullscreen && (
                  <button
                    className="subProductDetails__btn subProductDetails__expandBtn"
                    onClick={toggleFullscreen}
                  >
                    <AiOutlineExpand size={24} />
                  </button>
                )}

                {isFullscreen && (
                  <div
                    className={`subProductDetails__fullscreen ${
                      isAnimating ? "scale-0" : "scale-100"
                    }`}
                  >
                    <button
                      className="subProductDetails__btn subProductDetails__closeBtn"
                      onClick={toggleFullscreen}
                    >
                      <AiOutlineClose size={22} />
                    </button>

                    <button
                      className="subProductDetails__btn subProductDetails__prevBtn"
                      onClick={handlePrevImage}
                    >
                      <AiOutlineLeft size={24} />
                    </button>

                    <div>
                      <img
                        src={subProduct.images?.[currentImageIndex]?.url}
                        alt=""
                        className="subProductDetails__fullscreenImage"
                      />
                    </div>

                    <button
                      className="subProductDetails__btn subProductDetails__nextBtn"
                      onClick={handleNextImage}
                    >
                      <AiOutlineRight size={24} />
                    </button>

                    <div className="subProductDetails__thumbnailsWrapper">
                      {subProduct.images?.map((thumbnail, index) => (
                        <div
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`subProductDetails__thumbnailItem ${
                            currentImageIndex === index ? "active" : ""
                          }`}
                        >
                          <img src={thumbnail.url} alt="" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handlePrevImage}
                  className="subProductDetails__btn subProductDetails__prevBtn"
                >
                  <AiOutlineLeft size={24} />
                </button>
                <button
                  onClick={handleNextImage}
                  className="subProductDetails__btn subProductDetails__nextBtn"
                >
                  <AiOutlineRight size={24} />
                </button>
              </div>

              <div className="subProductDetails__bottomThumbnails">
                {subProduct.images?.map((img, index) => (
                  <div
                    className="subProductDetails__bottomThumbnails__thumbItem"
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img src={img.url} alt="" />
                  </div>
                ))}
              </div>
            </div>

            <div className="subProductDetails__infoBlock">
              <h1 className="subProductDetails__infoBlock__infoTitle">
                {subProduct.name}
              </h1>

              {hasDiscount ? (
                <>
                  <div className="subProductDetails__infoBlock__priceLine">
                    <h3 className="subProductDetails__infoBlock__priceLine__oldPrice">
                      <del>{formatPrice(totalOriginalPrice)} грн</del>
                    </h3>
                  </div>
                  <div className="subProductDetails__infoBlock__priceBlock">
                    <h4 className="subProductDetails__infoBlock__priceBlock__discountPrice">
                      {formatPrice(totalDiscountPrice)} грн
                    </h4>
                    <div
                      className={`subProductDetails__infoBlock__priceBlock__wishlistIcon ${
                        isInWishlist ? "active" : ""
                      }`}
                    >
                      {isInWishlist ? (
                        <AiFillHeart
                          onClick={handleRemoveFromWishlist}
                          title="Remove from wishlist"
                          style={{
                            animation: animateHeart
                              ? "heartbeat 0.8s ease-in-out infinite"
                              : "none",
                          }}
                        />
                      ) : (
                        <AiOutlineHeart
                          onClick={handleAddToWishlist}
                          title="Add to wishlist"
                        />
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="subProductDetails__infoBlock__priceBlock">
                  <h4 className="subProductDetails__infoBlock__priceBlock__discountPrice">
                    {formatPrice(totalOriginalPrice)} грн
                  </h4>
                  <div
                    className={`subProductDetails__infoBlock__priceBlock__wishlistIcon ${
                      isInWishlist ? "active" : ""
                    }`}
                  >
                    {isInWishlist ? (
                      <AiFillHeart
                        onClick={handleRemoveFromWishlist}
                        title="Remove from wishlist"
                        style={{
                          animation: animateHeart
                            ? "heartbeat 0.8s ease-in-out infinite"
                            : "none",
                        }}
                      />
                    ) : (
                      <AiOutlineHeart
                        onClick={handleAddToWishlist}
                        title="Add to wishlist"
                      />
                    )}
                  </div>
                </div>
              )}

              {subProduct.colors && subProduct.colors.length > 0 && (
                <div className="subProductDetails__infoBlock__colorsBlock">
                  <div>
                    <h4 className="subProductDetails__infoBlock__colorsBlock__colorsLabel">
                      Колір:
                    </h4>
                    {subProduct.colors.map((color, index) => (
                      <div
                        key={index}
                        className={`subProductDetails__infoBlock__colorsBlock__colorSquare ${
                          selectedColor === color ? "selected" : ""
                        }`}
                        style={{ backgroundColor: color }}
                        onMouseEnter={() => setHoveredColor(color)}
                        onMouseLeave={() => setHoveredColor(null)}
                        onClick={() => handleColorClick(color)}
                      >
                        {hoveredColor === color && (
                          <div
                            className="subProductDetails__infoBlock__colorsBlock__colorSquare__colorTooltip"
                            style={{ backgroundColor: color }}
                          >
                            <span>{color}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="subProductDetails__infoBlock__buttonsBlock">
                <div
                  className="subProductDetails__infoBlock__buttonsBlock__button"
                  onClick={handleAddToCart}
                >
                  Оформити кредит
                </div>
                <div
                  className="subProductDetails__infoBlock__buttonsBlock__button"
                  onClick={handleAddToCart}
                >
                  Додати в кошик
                </div>
              </div>
            </div>
          </div>

          {/* Якщо є додаткові блоки з інформацією – можна підключити */}
          {/* <ProductDetailsInfo data={subProduct} products={products} /> */}
        </div>
      </div>
    </div>
  );
};

export default SubProductDetails;
